import create from 'zustand';
import produce from 'immer';
import { nanoid } from 'nanoid';

import { getTask, getQuestion, getOption, getWord } from 'utils/Task';

const TASK_CREATE_INITIAL = {
	task: {
		id: 0,
		examId: 0,
		points: '',
		sample: '',
		type: 'quiz',
		description: '',
		order: '',
		questions: []
	},

	generator: {
		questions: '',
		options: ''
	}
};

export const useTaskCreateStore = create(set => ({
	...TASK_CREATE_INITIAL,

	// Handle state change
	onChange: ({ name, value }) => set(produce(state => {
		state.task[name] = value;
		// Reset task's questions on type change
		if (name === 'type') state.task.questions = [];
	})),

	// Handle question addition
	onAddQuestion: () => set(state => ({
		task: {
			...state.task,
			questions: [
				...state.task.questions,
				getQuestion(state.task.type)
			]
		}
	})),

	// Handle question removal
	onRemoveQuestion: (key) => set(state => ({
		task: {
			...state.task,
			questions: state.task.questions.filter(question => question.key !== key)
		}
	})),

	// Handle question change
	onChangeQuestion: (key, { name, value }) => set(produce(state => {
		const question = state.task.questions.find(q => q.key === key);
		question[name] = value;
	})),

	// Handle option addition
	onAddOption: (key) => set(produce(state => {
		const question = state.task.questions.find(q => q.key === key);
		question.options.push(getOption());
	})),

	// Handle option removal
	onRemoveOption: (key, optionKey) => set(produce(state => {
		const question = state.task.questions.find(q => q.key === key);
		question.options = question.options.filter(option => option.key !== optionKey);
	})),

	// Handle option change
	onChangeOption: (key, optionKey, { name, value }) => set(produce(state => {
		const question = state.task.questions.find(q => q.key === key);
		const option = question.options.find(o => o.key === optionKey);
		option[name] = value;
	})),

	// Handle word change
	onChangeWord: (key, { name, value }) => set(produce(state => {
		const question = state.task.questions.find(q => q.key === key);
		const word = question.words.find(w => w.key === name);
		word.value = value;
	})),

	// Handle word addition
	onAddWord: (key, wordKey) => set(produce(state => {
		const question = state.task.questions.find(q => q.key === key);
		question.words.push(getWord(wordKey));
	})),

	// Handle word removal
	onRemoveWord: (key, wordKey) => set(produce(state => {
		const question = state.task.questions.find(q => q.key === key);
		question.words = question.words.filter(word => word.key !== wordKey);
	})),

	// Handle generator change
	onChangeGenerator: ({ name, value }) => set(state => ({
		generator: {
			...state.generator,
			[name]: value
		}
	})),

	// Handle generator execution
	onInitGenerator: () => set(state => {
		// Do not generate task if required parameters are not met
		if (state.generator.questions === 0 ||
			(state.task.type === 'quiz' && state.generator.options === 0)) return state;

		return {
			task: {
				...state.task,
				questions: getTask(state.task.type, state.generator.questions, state.generator.options)
			}
		};
	}),

	// Initialize exam's state
	onInit: (task) => set({
		task: {
			id: task.id,
			examId: task.examId,
			points: task.points,
			sample: task.sample,
			type: task.type,
			description: task.description,
			order: task.order,
			questions: task?.questions?.map(question => {
				switch (task.type) {
				case 'quiz':
					return {
						id: question.id,
						key: nanoid(),
						content: question.content,
						options: question?.options?.map(option => ({
							id: option.id,
							key: nanoid(),
							content: option.content,
							correct: option.correct
						}))
					};
				case 'spellcheck':
					return {
						id: question.id,
						key: nanoid(),
						content: question.content,
						words: question?.words?.map(word => ({
							id: word.id,
							key: word.key,
							value: word.value
						}))
					};
				case 'connect':
					return {
						id: question.id,
						key: nanoid(),
						content: question.content,
						answer: question.answer
					};
				case 'essay':
				case 'multiple':
				case 'upload':
					return {
						id: question.id,
						key: nanoid(),
						content: question.content,
					};
				default:
					return {};
				}
			})
		}
	}),

	// Reset store to initial state
	onReset: () => set({
		...TASK_CREATE_INITIAL
	})
}));